<template>
  <main class="w-full h-full overflow-hidden">
    <ModalsAuthModals
      :has-auth-error="hasAuthError"
      :has-permissions-error="hasPermissionsError"
      :hide-close="true"
      project="this content"
      content="Deck"
    />
    <LayoutPassword v-if="needsPassword" @submit="submitPassword" />
    <div v-else-if="deck" class="flex flex-col w-screen h-dvh">
      <DeckAdminConsole
        v-if="deck && deckUserType !== 'controlled'"
        v-show="!currentlyRecording"
        :deck="deck"
        :class="{ hidden: isAutoPlaying }"
        @start-auto-play="startAutoPlay"
        @start-recording="openDeckControls"
      />

      <Deck
        :key="deck.id"
        class="flex-1"
        :class="{ recording: currentlyRecording }"
        analytics-event-name="Deck"
        :deck="deck"
        :deck-user-type="deckUserType as DeckUserType"
        :current-slide-index="currentSlideIndex"
        :slide-refresh-key="slideRefreshKey"
        :first-valid-slide="firstValidSlide"
        :last-valid-slide="lastValidSlide"
        :uuid="uuid"
        :is-embedded="false"
        :user="userState.user"
        :site="site"
        :slide-view-type="slideViewType"
        :is-auto-playing="isAutoPlaying"
        :three-d-viewer-error="threeDViewer.error.value"
        :three-d-viewer="threeDViewer"
        @set-current-slide-index="setCurrentSlideIndex"
        @start-auto-play="startAutoPlay"
        @pause-autoplay="pauseAutoPlay"
        @resume-autoplay="resumeAutoPlay"
        @navigation-previous-slide="previousSlide"
        @navigation-next-slide="nextSlide"
        @exit="handleExit"
        @goto:hotspot="setSelectedHotspot"
        @refetch-file="refetchHighResImages"
        @slide-ready="onSlideReady"
      />
    </div>
    <Portal to="participant-toast">
      <VNotification
        class="auto-play-toast"
        :show="isAutoPlaying"
        focused="focused"
        :dismissible="false"
        :delay="5000"
        translucent="translucent"
        data-testid="auto-play-toast"
      >
        <template #icon>
          <InformationCircleIcon class="_text-gray-900" />
        </template>
        <template #title> Autoplay will automatically advance through all slides. Tap anywhere to stop.</template>
      </VNotification>
    </Portal>
  </main>
</template>

<script lang="ts" setup>
import { useDeck, UseDeckAuthError, UseDeckPermissionsError, UseDeckPasswordRequiredError } from '@/composables/useDeck'
import { InformationCircleIcon } from '@heroicons/vue/24/solid'
import { sha512 } from 'js-sha512'
import type { CameraData } from '~/entities/model3d'
import type Record from '~/components/icons/Record.svg'
import { RemoteControlMessagesIn, RemoteControlMessagesOut } from '~/composables/useDeckRecorder'
import type { DeckUserType } from '~/entities/deck'

const { $sentry } = useNuxtApp()
const { state: userState } = useUser()
const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))

definePageMeta({
  layout: 'isolated-page',
})

const hasAuthError = computed(() => error.value instanceof UseDeckAuthError)
const hasPermissionsError = computed(() => error.value instanceof UseDeckPermissionsError)
const needsPassword = computed(() => error.value instanceof UseDeckPasswordRequiredError)

const { $analytics } = useNuxtApp()
const router = useRouter()
const route = useRoute()
const authIdCookie = useCookie(`auth${route.params.id}`)
const { showAlert, hideAlert } = useAlerts()

const uuid = (route.query?.auth as string) ?? null

const deckUserType = (route.query?.user_type as string) || ''

const {
  error,
  deck,
  currentSlideIndex,
  setCurrentSlideIndex,
  onStartAutoPlay,
  onCycleNextSlide,
  slideRefreshKey,
  setSelectedHotspot,
  previousSlide,
  nextSlide,
  submitPassword: callSubmitPassword,
  firstValidSlide,
  lastValidSlide,
  site,
  refetchHighResImages,
} = useDeck({
  id: toRef(route.params, 'id') as Ref<string>,
  user: ref(userState.value.user),
  include: ['company', 'site_image', 'model_camera'],
})

const priorityCameras = ref([] as CameraData[])
const threeDViewer = useThreeDViewer({ priorityCameras })

useDeckSetActiveThreeDScene({
  slides: computed(() => deck.value?.slides),
  currentSlideIndex,
  slideRefreshKey,
  setActiveScene: threeDViewer.setActiveScene,
})

const { isAutoPlaying, startAutoPlay, pauseAutoPlay, resumeAutoPlay } = useAutoPlay({
  onStart: onStartAutoPlay,
  onCycleNext: onCycleNextSlide,
})

const slideViewType = computed(() => {
  const currentSlideValue = deck.value?.slides[currentSlideIndex.value]
  if (currentSlideValue?.type === 'S') {
    if (site.value.selectedHotspot?.type === 'I') return 'I'
    if (site.value.selectedHotspot?.type === 'P') return 'P'
    if (site.value.selectedHotspot?.type === 'M') return 'M'
  }
  return currentSlideValue?.type
})

async function submitPassword(params: { password: string }) {
  $analytics.track('deckPasswordSubmitted', _parseAnalyticsPayload(route.params.id))
  hideAlert()

  try {
    authIdCookie.value = sha512(params.password)
    await callSubmitPassword(params)
  } catch (e) {
    $analytics.track('deckPasswordFailure', _parseAnalyticsPayload(route.params.id))

    showAlert({
      alertType: 'danger',
      alertMessage: 'Sorry, the password entered is incorrect. Please check the password and try again.',
    })
  }
}

function onSlideReady(isReady: boolean) {
  if (isReady) setTimeout(() => document.dispatchEvent(new CustomEvent('deck-slide-ready')), 1000)
}

function handleExit() {
  navigateTo({
    name: 'decks-id-feedback',
    params: { id: `${deck.value?.id}` },
    query: { auth: route.query.auth },
  })
}

function _parseAnalyticsPayload(identifier: any) {
  if (isNaN(parseInt(identifier))) {
    return { urlSlug: identifier }
  }

  return { deckId: parseInt(identifier) }
}

watch(deck, (newDeck, oldDeck) => {
  // Only run the first time deck is loaded
  if (!oldDeck && newDeck) {
    const querySlideId = route.query.slide
    if (querySlideId) {
      const slideIndex = newDeck.slides.findIndex(({ id }) => `${id}` === querySlideId)
      setCurrentSlideIndex(slideIndex && slideIndex >= 0 ? slideIndex : firstValidSlide.value)

      const query = Object.assign({}, route.query)
      delete query.slide

      router.replace({ query }).catch(e => {
        // handle route failure and do nothing on duplicates:
        // https://github.com/vuejs/vue-router/issues/2872
      })
    }
  }
})

watch(deck, async newDeck => {
  if (newDeck) {
    const data = await $fetch<{ results: CameraData[] }>(`/v2/decks/${newDeck.id}/priority_cameras?limit=5000`, {
      ...useFetchSharedOptions(),
      headers: {
        ...globalFetchHeaders.value,
        'x-deck-password': authIdCookie.value ? authIdCookie.value : 'null',
        'X-Deck-Auth-UUID': (route.query?.auth as string) ?? 'null',
        'X-Deck-Id': `${newDeck.id ?? null}`,
      },
    })
    priorityCameras.value = data.results
  }
})

watchEffect(() => {
  globalFetchHeaders.value['X-Deck-Auth-UUID'] = (route?.query?.auth as string) ?? null
  globalFetchHeaders.value['X-Deck-Id'] = `${deck.value?.id ?? null}`
})

const { openRemoteDeckControls, startRecording, stopRecording } = useDeckRecorder()

const remoteControls = ref<WindowProxy | null>(null)
const currentlyRecording = ref<boolean>(false)

const userFlaggedForRecordingTool = computed(() => userState.value.flags.DECK_RECORDING_ENABLED === true)
function openDeckControls() {
  if (!userFlaggedForRecordingTool.value) return
  try {
    remoteControls.value = openRemoteDeckControls({
      slide: currentSlideIndex.value,
    })

    remoteControls.value?.window.addEventListener('load', () => {
      currentlyRecording.value = true
      remoteControls.value?.window.addEventListener('unload', () => {
        currentlyRecording.value = false
        remoteControls.value = null
      })
    })
  } catch (e) {
    $sentry?.captureException(e)
    return
  }

  window.addEventListener('message', messageEventHandler)
}

const messageHandlers: Record<string, (payload: any) => void> = {
  [RemoteControlMessagesOut.Loaded]: () => {
    remoteControls.value?.postMessage({ name: RemoteControlMessagesIn.SetDeck, payload: toRaw(deck.value) }, window.location.origin)
  },
  [RemoteControlMessagesOut.SetSlide]: (payload: number) => {
    setCurrentSlideIndex(payload)
  },
  [RemoteControlMessagesOut.Start]: async () => {
    try {
      await startRecording()
    } catch (e) {
      $sentry.captureException(e)
    }
  },
  [RemoteControlMessagesOut.Stop]: () => {
    stopRecording()
  },
}
function messageEventHandler(event: MessageEvent<{ name: string; payload: any }>) {
  const { origin, source, data } = event
  if (origin !== window.location.origin) return
  if ((source as WindowProxy)?.name !== 'RemoteDeckControls') return
  const { name = '', payload } = data
  if (name in messageHandlers) {
    messageHandlers[name](payload)
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('message', messageEventHandler)
  remoteControls.value?.close()
})

onUnmounted(() => {
  delete globalFetchHeaders.value['X-Deck-Auth-UUID']
})
</script>

<style lang="stylus" scoped>
.auto-play-toast
  @apply w-80
@screen md
  .auto-play-toast
    width 29rem
</style>
